import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';
import { Participante, Racha, Despesa, Divisao } from '@/types/app';
import { rachaService } from '@/services/rachaService';
import { criarDespesa, obterDespesa, obterDespesasPorRacha, atualizarDespesa, excluirDespesa } from '@/services/despesaService';
import { migrateData, MigrationProgress } from '@/utils/migrateData';
import { logger } from '@/utils/logger';

interface RachaContextType {
  rachas: Racha[];
  rachasAtivos: Racha[];
  rachasFinalizados: Racha[];
  createRacha: (nome: string, emoji: string, participantesSelecionados?: Participante[]) => Promise<string>;
  updateRacha: (id: string, updates: Partial<Racha>) => Promise<void>;
  deleteRacha: (id: string) => Promise<void>;
  finalizarRacha: (id: string) => Promise<void>;
  reativarRacha: (id: string) => Promise<void>;
  getRacha: (id: string) => Racha | undefined;
  addDespesa: (rachaId: string, despesa: Omit<Despesa, 'id'>) => Promise<void>;
  updateDespesa: (rachaId: string, despesaId: string, updates: Partial<Despesa>) => Promise<void>;
  deleteDespesa: (rachaId: string, despesaId: string) => Promise<void>;
  addParticipanteToRacha: (rachaId: string, participante: Participante) => Promise<void>;
  removeParticipanteFromRacha: (rachaId: string, participanteId: string) => Promise<void>;
  getTotalDespesas: (rachaId: string) => number;
  loading: boolean;
  // Novas funções para migração
  migrationProgress: MigrationProgress | null;
  startMigration: () => Promise<void>;
  useFirestore: boolean;
  // Modal de migração
  showMigrationModal: boolean;
  setShowMigrationModal: (show: boolean) => void;
  // Modal de limite de rachas
  showLimiteModal: boolean;
  setShowLimiteModal: (show: boolean) => void;
  limiteRachas: number;
}

const RachaContext = createContext<RachaContextType | undefined>(undefined);

// Avatar padrão para participantes
const AVATAR_PADRAO = 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2';

export function RachaProvider({ children }: { children: React.ReactNode }) {
  const [rachas, setRachas] = useState<Racha[]>([]);
  const [loading, setLoading] = useState(true);
  const [migrationProgress, setMigrationProgress] = useState<MigrationProgress | null>(null);
  const [useFirestore, setUseFirestore] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [showLimiteModal, setShowLimiteModal] = useState(false);
  const { user } = useAuth();

  const LIMITE_RACHAS_GRATUITO = 1;

  useEffect(() => {
    if (user) {
      initializeRachas();
    }
  }, [user]);

  // Listener para sincronização em tempo real
  useEffect(() => {
    if (useFirestore && user) {
      logger.debug('Configurando listener do Firestore para usuário:', user.id);
      const unsubscribe = rachaService.subscribeToUserRachas(user.id, (rachasAtualizados) => {
        logger.debug('Rachas atualizados via listener:', rachasAtualizados.length);
        setRachas(rachasAtualizados);
        setLoading(false);
      });

      return unsubscribe;
    }
  }, [useFirestore, user]);

  const rachasAtivos = rachas.filter(racha => racha.status === 'ativo');
  const rachasFinalizados = rachas.filter(racha => racha.status === 'finalizado');

  // Função principal para inicializar rachas
  const initializeRachas = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // 1. Verificar se já foi migrado para Firestore
      const isMigrated = await migrateData.isMigrationCompleted(user.id);

      if (isMigrated) {
        logger.debug('Usuário já migrado, usando Firestore');
        setUseFirestore(true);
        // Os dados serão carregados pelo listener do useEffect
      } else {
        // 2. Verificar se há dados locais
        const hasLocalData = await migrateData.hasLocalData(user.id);

        if (hasLocalData) {
          logger.debug('Dados locais encontrados, carregando do AsyncStorage');
          await loadRachasFromAsyncStorage();
          // Oferecer migração automática
          setTimeout(() => {
            logger.debug('Oferecendo migração automática');
            setShowMigrationModal(true);
          }, 1000);
        } else {
          logger.debug('Nenhum dado encontrado, usando Firestore para novos dados');
          setUseFirestore(true);
          setLoading(false);
        }
      }
    } catch (error) {
      logger.error('Erro ao inicializar rachas:', error);
      // Fallback para AsyncStorage
      await loadRachasFromAsyncStorage();
    }
  };

  // Carregar rachas do AsyncStorage (método antigo)
  const loadRachasFromAsyncStorage = async () => {
    try {
      const storedRachas = await AsyncStorage.getItem(`@rachas_${user?.id}`);
      if (storedRachas) {
        const parsedRachas = JSON.parse(storedRachas).map((racha: any) => ({
          ...racha,
          status: racha.status || 'ativo',
          dataFinalizacao: racha.dataFinalizacao && typeof racha.dataFinalizacao === 'object' && 'seconds' in racha.dataFinalizacao && 'nanoseconds' in racha.dataFinalizacao
            ? new Date((racha.dataFinalizacao as { seconds: number, nanoseconds: number }).seconds * 1000 + Math.floor((racha.dataFinalizacao as { seconds: number, nanoseconds: number }).nanoseconds / 1000000))
            : racha.dataFinalizacao ? new Date(racha.dataFinalizacao) : undefined,
          despesas: racha.despesas.map((despesa: any) => {
            if (despesa.data && typeof despesa.data === 'object' && 'seconds' in despesa.data && 'nanoseconds' in despesa.data) {
              const ts = despesa.data as { seconds: number, nanoseconds: number };
              return {
                ...despesa,
                data: new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1000000))
              };
            }
            return {
              ...despesa,
              data: new Date(despesa.data)
            };
          }),
          gruposDeBolso: racha.gruposDeBolso || []
        }));

        logger.debug(`Carregados ${parsedRachas.length} rachas do AsyncStorage`);
        setRachas(parsedRachas);
      }
    } catch (error) {
      logger.error('Erro ao carregar rachas do AsyncStorage:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveRachas = async (novosRachas: Racha[]) => {
    try {
      await AsyncStorage.setItem(`@rachas_${user?.id}`, JSON.stringify(novosRachas));
    } catch (error) {
      logger.error('RachaContext: Erro ao salvar rachas:', error);
      throw error;
    }
  };

  const createRacha = async (nome: string, emoji: string, participantesSelecionados?: Participante[]): Promise<string> => {
    logger.debug('🔥 RachaContext.createRacha: Iniciando');
    logger.debug('👤 Usuário:', user);
    logger.debug('📝 Parâmetros recebidos:', { nome, emoji, participantesSelecionados });

    if (!user) {
      logger.debug('❌ Usuário não autenticado');
      throw new Error('Usuário não autenticado');
    }

    // Verificar limite de rachas para usuários gratuitos
    logger.debug('🔍 Verificando se usuário é premium...');
    logger.debug('👤 Plano do usuário:', user.plano || 'gratuito');

    // Verificação de premium (Vitalício ou Anual)
    const isUserPremium = user.plano === 'vitalicio' || user.plano === 'anual';

    logger.debug('💎 É premium?', isUserPremium);

    if (!isUserPremium) {
      logger.debug('👤 Usuário gratuito, verificando limite de rachas...');
      const rachasAtivos = rachas.filter(r => r.status === 'ativo');
      logger.debug('📊 Rachas ativos:', rachasAtivos.length, 'de', LIMITE_RACHAS_GRATUITO);
      logger.debug('📋 Lista de rachas ativos:', rachasAtivos.map(r => ({ id: r.id, nome: r.nome })));

      if (rachasAtivos.length >= LIMITE_RACHAS_GRATUITO) {
        logger.debug('🚫 Limite de rachas atingido - mostrando modal');
        setShowLimiteModal(true);
        return '';
      } else {
        logger.debug('✅ Limite não atingido, pode criar racha');
      }
    } else {
      logger.debug('💎 Usuário premium, sem limite de rachas');
    }

    try {
      logger.debug('🔄 Processando participantes...');
      const participantesIniciais = participantesSelecionados || [{
        id: user.id,
        nome: user.nome,
        foto: AVATAR_PADRAO,
        chavePix: user.chavePix
      }];
      logger.debug('👥 Participantes iniciais:', participantesIniciais);

      // Garantir que o usuário atual esteja sempre incluído
      const usuarioJaIncluido = participantesIniciais.some(p => p.id === user.id);
      logger.debug('✅ Usuário já incluído?', usuarioJaIncluido);

      if (!usuarioJaIncluido) {
        logger.debug('➕ Adicionando usuário atual aos participantes');
        participantesIniciais.unshift({
          id: user.id,
          nome: user.nome,
          foto: AVATAR_PADRAO,
          chavePix: user.chavePix
        });
      }

      // Garantir que todos os participantes tenham avatar padrão
      const participantesComAvatar = participantesIniciais.map(p => ({
        ...p,
        foto: AVATAR_PADRAO
      }));
      logger.debug('🖼️ Participantes com avatar:', participantesComAvatar);

      logger.debug('🔥 useFirestore:', useFirestore);
      if (useFirestore) {
        logger.debug('🔥 Usando Firestore para criar racha');
        // Usar Firestore
        logger.debug('Criando racha no Firestore');

        // Criar objeto do racha com valores seguros
        const novoRacha: Racha = {
          id: Date.now().toString(),
          nome: nome.trim(),
          emoji: emoji || '💸',
          criadorId: user.id,
          participantes: participantesComAvatar,
          despesas: [],
          ultimaAtividade: 'Criado agora',
          status: 'ativo',
          gruposDeBolso: [],
          dataCriacao: new Date()
        };

        // Preparar dados seguros para o Firestore
        const dadosParaSalvar = {
          nome: novoRacha.nome,
          emoji: novoRacha.emoji,
          criadorId: novoRacha.criadorId,
          participantes: novoRacha.participantes.map(p => ({
            id: p.id,
            nome: p.nome || '',
            email: p.email || '',
            foto: p.foto || AVATAR_PADRAO,
            chavePix: p.chavePix || undefined
          })),
          despesas: [],
          ultimaAtividade: novoRacha.ultimaAtividade,
          status: novoRacha.status,
          gruposDeBolso: [],
          dataCriacao: novoRacha.dataCriacao
        } as Partial<Racha>;

        // Log para depuração
        logger.debug('📦 Dados do racha preparados:', dadosParaSalvar);

        logger.debug('🚀 Chamando rachaService.createRacha...');
        // Criar no Firestore
        const result = await rachaService.createRacha(dadosParaSalvar);
        logger.debug('✅ Resultado do rachaService.createRacha:', result);

        if (result.success && result.data?.id) {
          logger.debug('✅ Racha criado no Firestore com ID:', result.data.id);
          // Atualizar o ID do racha com o retornado pelo Firestore
          novoRacha.id = result.data.id;

          logger.debug('🔄 Atualizando estado local com novo racha');
          // Atualizar o estado local
          const novosRachas = [novoRacha, ...rachas];
          setRachas(novosRachas);
          logger.debug('✅ Estado local atualizado');

          return novoRacha.id;
        } else {
          logger.debug('❌ Erro ao criar racha no Firestore:', result.error);
          throw new Error(result.error || 'Erro desconhecido ao criar racha no Firestore');
        }
      } else {
        logger.debug('💾 Usando AsyncStorage para criar racha');
        // Usar AsyncStorage
        logger.debug('Criando racha no AsyncStorage');

        const novoRacha: Racha = {
          id: Date.now().toString(),
          nome: nome.trim(),
          emoji: emoji || '💸',
          criadorId: user.id,
          participantes: participantesComAvatar,
          despesas: [],
          ultimaAtividade: 'Criado agora',
          status: 'ativo',
          gruposDeBolso: [],
          dataCriacao: new Date()
        };

        logger.debug('📦 Novo racha criado:', { id: novoRacha.id, nome: novoRacha.nome });
        logger.debug('💾 Salvando no AsyncStorage...');

        const novosRachas = [novoRacha, ...rachas];
        setRachas(novosRachas);
        await saveRachas(novosRachas);

        logger.debug('✅ Racha salvo no AsyncStorage com sucesso');
        return novoRacha.id;
      }
    } catch (error) {
      logger.error('❌ RachaContext: Erro ao criar racha:', error);
      logger.error('❌ Stack trace:', error);
      return '';
    }
  };

  const updateRacha = async (id: string, updates: Partial<Racha>) => {
    try {
      logger.debug('=== DEBUG updateRacha ===');
      logger.debug('ID do racha:', id);
      logger.debug('Updates recebidos:', updates);
      logger.debug('Usando Firestore:', useFirestore);

      if (useFirestore) {
        logger.debug('Atualizando racha no Firestore:', id);
        logger.debug('Dados a serem enviados para o Firestore:', updates);

        const result = await rachaService.updateRacha(id, updates);
        logger.debug('Resultado da atualização no Firestore:', result);

        if (!result.success) {
          logger.error('Erro retornado pelo rachaService:', result.error);
          throw new Error(result.error);
        }

        logger.debug('✅ Atualização no Firestore bem-sucedida');
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Atualizando racha no AsyncStorage:', id);
        logger.debug('Rachas atuais no estado:', rachas.length);

        const rachaAntes = rachas.find(r => r.id === id);
        logger.debug('Racha antes da atualização:', rachaAntes ? { id: rachaAntes.id, nome: rachaAntes.nome } : 'não encontrado');

        const novosRachas = rachas.map(racha =>
          racha.id === id ? { ...racha, ...updates, ultimaAtividade: 'Agora' } : racha
        );

        const rachaDepois = novosRachas.find(r => r.id === id);
        logger.debug('Racha depois da atualização:', rachaDepois ? { id: rachaDepois.id, nome: rachaDepois.nome } : 'não encontrado');

        setRachas(novosRachas);
        await saveRachas(novosRachas);
        logger.debug('✅ Atualização no AsyncStorage bem-sucedida');
      }

      logger.debug('=== FIM DEBUG updateRacha ===');
    } catch (error) {
      logger.error('RachaContext: Erro ao atualizar racha:', error);
      logger.error('Stack trace:', error);
      throw error;
    }
  };

  const deleteRacha = async (id: string) => {
    try {
      if (useFirestore) {
        logger.debug('Deletando racha no Firestore:', id);
        const result = await rachaService.deleteRacha(id, user!.id);
        if (!result.success) {
          throw new Error(result.error);
        }
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Deletando racha no AsyncStorage:', id);
        const novosRachas = rachas.filter(racha => racha.id !== id);
        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao excluir racha:', error);
      throw error;
    }
  };

  const finalizarRacha = async (id: string) => {
    try {
      if (useFirestore) {
        logger.debug('Finalizando racha no Firestore:', id);
        const result = await rachaService.finalizarRacha(id);
        if (!result.success) {
          throw new Error(result.error);
        }
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Finalizando racha no AsyncStorage:', id);
        const novosRachas = rachas.map(racha =>
          racha.id === id ? {
            ...racha,
            status: 'finalizado' as const,
            dataFinalizacao: new Date(),
            ultimaAtividade: 'Finalizado'
          } : racha
        );

        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao finalizar racha:', error);
      throw error;
    }
  };

  const reativarRacha = async (id: string) => {
    try {
      if (useFirestore) {
        logger.debug('Reativando racha no Firestore:', id);
        const result = await rachaService.reativarRacha(id);
        if (!result.success) {
          throw new Error(result.error);
        }
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Reativando racha no AsyncStorage:', id);
        const novosRachas = rachas.map(racha =>
          racha.id === id ? {
            ...racha,
            status: 'ativo' as const,
            dataFinalizacao: undefined,
            ultimaAtividade: 'Reativado'
          } : racha
        );
        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao reativar racha:', error);
      throw error;
    }
  };

  const getRacha = (id: string): Racha | undefined => {
    return rachas.find(racha => racha.id === id);
  };

  const addDespesa = async (rachaId: string, despesa: Omit<Despesa, 'id'>) => {
    try {
      if (useFirestore) {
        logger.debug('Adicionando despesa no Firestore:', rachaId);

        // Primeiro, verifica se o racha existe e obtém seus dados atuais
        const rachaResult = await rachaService.getRachaById(rachaId);
        if (!rachaResult.success || !rachaResult.data) {
          throw new Error(`Racha não encontrado: ${rachaResult.error}`);
        }

        // Validar e limpar os dados da nova despesa
        let dataDespesa: Date;
        if (despesa.data && typeof despesa.data === 'object' && ('seconds' in despesa.data && 'nanoseconds' in despesa.data)) {
          // Firestore Timestamp
          const ts = despesa.data as { seconds: number, nanoseconds: number };
          dataDespesa = new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1000000));
        } else if (despesa.data instanceof Date) {
          dataDespesa = despesa.data;
        } else if (typeof despesa.data === 'string' && !isNaN(Date.parse(despesa.data))) {
          dataDespesa = new Date(despesa.data);
        } else {
          dataDespesa = new Date();
        }
        const novaDespesa: Despesa = {
          id: Date.now().toString(),
          titulo: despesa.titulo || '',
          valor: typeof despesa.valor === 'number' ? despesa.valor : 0,
          data: dataDespesa,
          pagoPorId: despesa.pagoPorId || '',
          pagoPorNome: despesa.pagoPorNome || '',
          modoDivisao: despesa.modoDivisao || 'igual',
          divisoes: Array.isArray(despesa.divisoes)
            ? despesa.divisoes.map(div => ({
              usuarioId: div.usuarioId || '',
              valorDevido: typeof div.valorDevido === 'number' ? div.valorDevido : 0
            }))
            : [],
          categoria: despesa.categoria || {
            id: 'default',
            nome: 'Geral',
            emoji: '💰',
            cor: '#666666',
            icone: 'dollar'
          }
        };

        // Preparar dados para atualização
        const despesasAtualizadas = [novaDespesa, ...(rachaResult.data.despesas || [])];
        const ultimaAtividade = new Date().toISOString();

        // Atualizar no Firestore
        const result = await rachaService.updateRacha(rachaId, {
          despesas: despesasAtualizadas,
          ultimaAtividade
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        // Atualizar o estado local se não houver listener ativo
        if (!rachaService.hasActiveListener) {
          const rachaAtualizado: Racha = {
            ...rachaResult.data,
            despesas: despesasAtualizadas,
            ultimaAtividade
          };
          setRachas(rachas.map(r => r.id === rachaId ? rachaAtualizado : r));
        }
      } else {
        logger.debug('Adicionando despesa no AsyncStorage:', rachaId);
        let dataDespesa: Date;
        if (despesa.data && typeof despesa.data === 'object' && ('seconds' in despesa.data && 'nanoseconds' in despesa.data)) {
          // Firestore Timestamp
          const ts = despesa.data as { seconds: number, nanoseconds: number };
          dataDespesa = new Date(ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1000000));
        } else if (despesa.data instanceof Date) {
          dataDespesa = despesa.data;
        } else if (typeof despesa.data === 'string' && !isNaN(Date.parse(despesa.data))) {
          dataDespesa = new Date(despesa.data);
        } else {
          dataDespesa = new Date();
        }
        const novaDespesa: Despesa = {
          ...despesa,
          id: Date.now().toString(),
          data: dataDespesa
        };

        const novosRachas = rachas.map(racha => {
          if (racha.id === rachaId) {
            return {
              ...racha,
              despesas: [novaDespesa, ...racha.despesas],
              ultimaAtividade: new Date().toISOString()
            };
          }
          return racha;
        });

        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao adicionar despesa:', error);
      throw error;
    }
  };

  const updateDespesa = async (rachaId: string, despesaId: string, updates: Partial<Despesa>) => {
    try {
      if (useFirestore) {
        logger.debug('Atualizando despesa no Firestore:', despesaId);
        const result = await atualizarDespesa(rachaId, despesaId, updates);
        if (!result) {
          throw new Error('Erro ao atualizar despesa');
        }
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Atualizando despesa no AsyncStorage:', despesaId);
        const novosRachas = rachas.map(racha => {
          if (racha.id === rachaId) {
            return {
              ...racha,
              despesas: racha.despesas.map(despesa =>
                despesa.id === despesaId ? { ...despesa, ...updates } : despesa
              ),
              ultimaAtividade: 'Agora'
            };
          }
          return racha;
        });
        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao atualizar despesa:', error);
      throw error;
    }
  };

  const deleteDespesa = async (rachaId: string, despesaId: string) => {
    try {
      if (useFirestore) {
        logger.debug('Deletando despesa no Firestore:', despesaId, 'tipo:', typeof despesaId);

        // Primeiro, obter os dados atuais do racha
        const rachaResult = await rachaService.getRachaById(rachaId);
        if (!rachaResult.success || !rachaResult.data) {
          throw new Error(`Racha não encontrado: ${rachaResult.error}`);
        }

        // Filtrar a despesa do array
        const despesasFiltradas = (rachaResult.data.despesas || []).filter(despesa => despesa.id !== despesaId);

        // Atualizar o documento principal com o array filtrado
        const result = await rachaService.updateRacha(rachaId, {
          despesas: despesasFiltradas,
          ultimaAtividade: 'Despesa excluída'
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Deletando despesa no AsyncStorage:', despesaId);
        const novosRachas = rachas.map(racha => {
          if (racha.id === rachaId) {
            const despesasFiltradas = racha.despesas.filter(despesa => despesa.id !== despesaId);

            return {
              ...racha,
              despesas: despesasFiltradas,
              ultimaAtividade: 'Agora'
            };
          }
          return racha;
        });

        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro em deleteDespesa:', error);
      throw error;
    }
  };

  const addParticipanteToRacha = async (rachaId: string, participante: Participante) => {
    try {
      // Garantir que o participante tenha avatar padrão
      const participanteComAvatar = {
        ...participante,
        foto: AVATAR_PADRAO
      };

      if (useFirestore) {
        logger.debug('Adicionando participante no Firestore:', participante.nome);
        const result = await rachaService.addParticipanteToRacha(rachaId, participanteComAvatar);
        if (!result.success) {
          throw new Error(result.error);
        }
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Adicionando participante no AsyncStorage:', participante.nome);

        // Obter o racha mais recente do estado
        const rachaAtual = rachas.find(r => r.id === rachaId);
        if (!rachaAtual) {
          throw new Error(`Racha com id ${rachaId} não encontrado no estado`);
        }

        logger.debug('Participantes atuais:', rachaAtual.participantes.map(p => p.id));
        logger.debug('ID do participante a adicionar:', participante.id);

        const participanteExiste = rachaAtual.participantes.some(p => p.id === participante.id);
        if (participanteExiste) {
          throw new Error('Participante já está no racha');
        }

        const novosParticipantes = [...rachaAtual.participantes, participanteComAvatar];
        const novosRachas = rachas.map(racha =>
          racha.id === rachaId ? { ...racha, participantes: novosParticipantes, ultimaAtividade: 'Agora' } : racha
        );

        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao adicionar participante:', error);
      throw error;
    }
  };

  const removeParticipanteFromRacha = async (rachaId: string, participanteId: string) => {
    try {
      if (useFirestore) {
        logger.debug('Removendo participante no Firestore:', participanteId);
        const result = await rachaService.removeParticipanteFromRacha(rachaId, participanteId);
        if (!result.success) {
          throw new Error(result.error);
        }
        // Os dados serão atualizados pelo listener
      } else {
        logger.debug('Removendo participante no AsyncStorage:', participanteId);
        const novosRachas = rachas.map(racha => {
          if (racha.id === rachaId && racha.criadorId !== participanteId) {
            // Remove participante and also remove from any expense divisions
            const participantesAtualizados = racha.participantes.filter(p => p.id !== participanteId);
            const despesasAtualizadas = racha.despesas.map(despesa => ({
              ...despesa,
              divisoes: Array.isArray(despesa.divisoes)
                ? despesa.divisoes.filter(div => div.usuarioId !== participanteId)
                : []
            }));
            return {
              ...racha,
              participantes: participantesAtualizados,
              despesas: despesasAtualizadas,
              ultimaAtividade: 'Agora'
            };
          }
          return racha;
        });
        setRachas(novosRachas);
        await saveRachas(novosRachas);
      }
    } catch (error) {
      logger.error('RachaContext: Erro ao remover participante:', error);
      throw error;
    }
  };

  const getTotalDespesas = (rachaId: string): number => {
    const racha = rachas.find(racha => racha.id === rachaId);
    if (!racha) return 0;
    return racha.despesas.reduce((total, despesa) => total + despesa.valor, 0);
  };

  // Função para iniciar migração
  const startMigration = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Verificar se já foi migrado
      const isMigrated = await migrateData.isMigrationCompleted(user.id);
      if (isMigrated) {
        logger.debug('Migração já foi concluída anteriormente');
        setUseFirestore(true);
        return;
      }

      // Executar migração
      const result = await migrateData.safeMigration(user.id, setMigrationProgress);

      if (result.success) {
        logger.debug('Migração concluída com sucesso');
        await migrateData.markMigrationCompleted(user.id);
        setUseFirestore(true);

        // Recarregar dados do Firestore
        await loadRachasFromFirestore();
      } else {
        logger.error('Erro na migração:', result.error);
      }
    } catch (error) {
      logger.error('Erro ao iniciar migração:', error);
    } finally {
      setLoading(false);
      setMigrationProgress(null);
    }
  };

  // Carregar rachas do Firestore
  const loadRachasFromFirestore = async () => {
    if (!user) return;

    try {
      const result = await rachaService.getUserRachas(user.id);
      if (result.success && result.data) {
        setRachas(result.data);
      }
    } catch (error) {
      logger.error('Erro ao carregar rachas do Firestore:', error);
    }
  };

  return (
    <RachaContext.Provider value={{
      rachas,
      rachasAtivos,
      rachasFinalizados,
      createRacha,
      updateRacha,
      deleteRacha,
      finalizarRacha,
      reativarRacha,
      getRacha,
      addDespesa,
      updateDespesa,
      deleteDespesa,
      addParticipanteToRacha,
      removeParticipanteFromRacha,
      getTotalDespesas,
      loading,
      migrationProgress,
      startMigration,
      useFirestore,
      showMigrationModal,
      setShowMigrationModal,
      showLimiteModal,
      setShowLimiteModal,
      limiteRachas: LIMITE_RACHAS_GRATUITO
    }}>
      {children}
    </RachaContext.Provider>
  );
}

export function useRacha() {
  const context = useContext(RachaContext);
  if (context === undefined) {
    throw new Error('useRacha must be used within a RachaProvider');
  }
  return context;
}
